import { parseFloatOrZero } from 'lib/parsers'
import {
  Invoice,
  ServerLineItem,
  LineItem,
  ServerLineItemGroup,
  AggregatedInvoice,
  AggregatedLineItemGroup,
  Customer,
  InvoiceMutation,
  ServerSubAccountUsage
} from './types'
import { mergeLineItemsIntoGroups } from 'modules/LineItems'
import { flattenSubAccountUsage } from '@sequencehq/invoice-content'

/**
 * TODO: handle ungrouped line items (the API typing has `groupId` as optional)
 */
type ServerLineItemWithGroup = ServerLineItem & { groupId: string }

export const lineItem = {
  fromServer: (serverLineItem: ServerLineItemWithGroup): LineItem => {
    return { ...serverLineItem }
  },
  toServer: (lineItem: LineItem): ServerLineItem => {
    const {
      rate,
      netTotal,
      servicePeriodStart,
      servicePeriodEnd,
      ...serverLineItem
    } = lineItem

    return {
      ...serverLineItem,
      rate: parseFloatOrZero(rate).toString(),
      netTotal: parseFloatOrZero(netTotal).toString()
    }
  }
}

export const lineItems = {
  fromServer: (serverLineItems: ServerLineItem[]) => {
    return filterInvalidLineItems(serverLineItems)
      .map(lineItem.fromServer)
      .reverse() // Server returns newest line items first
  },
  toServer: (clientLineItems: LineItem[]): ServerLineItem[] => {
    return clientLineItems.map(lineItem.toServer)
  }
}

export const lineItemGroups = {
  fromServer: (
    lineItemGroups: ServerLineItemGroup[],
    serverLineItems: ServerLineItem[]
  ): AggregatedLineItemGroup[] => {
    return mergeLineItemsIntoGroups(
      lineItems.fromServer(serverLineItems),
      lineItemGroups
    )
  }
}

export const invoice = {
  fromServer: (
    serverInvoice: Invoice,
    serverLineItemGroups: ServerLineItemGroup[],
    serverLineItems: ServerLineItem[],
    serverCustomer: Customer,
    subAccountUsage: ServerSubAccountUsage[]
  ): AggregatedInvoice => {
    return {
      ...serverInvoice,
      /**
       * Pass dueDate explicitly, even if its undefined, to ensure `dueDate`
       * is cleared on the clientside when unset by the backend
       */
      dueDate: serverInvoice.dueDate,
      lineItemGroups: lineItemGroups.fromServer(
        serverLineItemGroups,
        serverLineItems
      ),
      customer: {
        integrationIds: serverCustomer.integrationIds
      },
      subAccountUsage: flattenSubAccountUsage(subAccountUsage)
    }
  },
  toServer: (clientInvoice: AggregatedInvoice): InvoiceMutation => {
    // remove unused properties from clientInvoice
    const {
      lineItemGroups,
      attachmentAssets,
      subAccountUsage,
      paymentStatus, // updated via a separate PUT endpoint
      createdAt,
      updatedAt,
      appliedCreditNotes, // legacy response field, never sent in a mutation
      paymentAllocations, // legacy response field, never sent in a mutation
      currentlyApplied, // read-only, derived from the ledger
      appliedCreditGrants, // read-only, derived from the ledger
      appliedCreditGrantTax, // read-only, derived from the ledger
      projectedCreditGrants, // read-only, derived from the ledger
      balance, // read-only, derived from the ledger
      dueDate, // read-only, derived from the due date policy
      paymentTermDays, // read-only, derived from the due date policy
      dueDateSource, // read-only, derived from the due date policy
      dueDateStatus, // read-only, derived from the due date policy
      customerBillingAddress,
      customerShippingAddress,
      ...serverInvoice
    } = clientInvoice as AggregatedInvoice & {
      appliedCreditNotes?: unknown
      paymentAllocations?: unknown
    }

    return {
      ...serverInvoice,
      // Both addresses are optional on the way out: the customer may have
      // none, and sending an explicit null is not the same as leaving the
      // invoice's existing address alone.
      ...(customerBillingAddress && { customerBillingAddress }),
      ...(customerShippingAddress && { customerShippingAddress }),
      attachmentAssetIds:
        clientInvoice.attachmentAssets?.map(asset => asset.id) ?? []
    }
  }
}

const filterInvalidLineItems = (lineItems: ServerLineItem[]) => {
  const filtered = lineItems.filter(
    lineItem => lineItem.groupId !== null
  ) as ServerLineItemWithGroup[]
  if (filtered.length !== lineItems.length) {
    console.error('filterInvalidLineItems: Invalid line items found')
  }
  return filtered
}
